import { useRef } from 'react';

import { Field, Image, ImageField, Link, LinkField, Text, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';

import { ComponentProps } from 'lib/ComponentProps';

import { ExtendedItem } from 'src/types/ExtendedItem';

import IsSitecoreEditing from 'src/utils/isSitecoreEditing';

import Accordion from 'src/components/basicContent/_Accordion/Accordion';
import { Button } from 'src/components/basicContent/_Button/Button';

import ChevronDownSvg from 'public/icons/ChevronDown.svg';
import FacebookSvg from 'public/icons/facebook.svg';
import InstagramSvg from 'public/icons/instagram.svg';
import LinkedInSvg from 'public/icons/linkedin.svg';
import YouTubeSvg from 'public/icons/youtube.svg';

import FooterBackToTop from './_FooterBackToTop';

import styles from './Footer.module.css';

type LinksField = [
	{
		fields: {
			Link: LinkField;
		};
	}
];

type FooterProps = ComponentProps & {
	fields: {
		LinksColumn1Heading: Field<string>;
		LinksColumn2Heading: Field<string>;
		LinksColumn3Heading: Field<string>;
		LinksColumn1: ExtendedItem & LinksField;
		LinksColumn2: ExtendedItem & LinksField;
		LinksColumn3: ExtendedItem & LinksField;
		RcnLogo: ImageField;
		RcnLogoLink: LinkField;
		RcnLearnLogo: ImageField;
		RcniLearningLogo: ImageField;
		ScrollToTopText: Field<string>;
		CopyrightText: Field<string>;
		ContactLink: LinkField;
		ContactColumnHeading: Field<string>;
		FacebookLink: LinkField;
		InstagramLink: LinkField;
		YouTubeLink: LinkField;
		LinkedInLink: LinkField;
		FacebookImage: ImageField;
		InstagramImage: ImageField;
		YoutubeImage: ImageField;
		LinkedinImage: ImageField;
	};
};

function toggleBanner() {
	if (globalThis?.OneTrust?.ToggleInfoDisplay) {
		globalThis.OneTrust.ToggleInfoDisplay();
	}
}

export default withDatasourceCheck()<FooterProps>(Footer);

function Footer(props: FooterProps): JSX.Element {
	const isEditing = IsSitecoreEditing();
	const currentYear = new Date().getFullYear();
	const cookieManagementBtn = useRef<HTMLButtonElement>(null);

	const listStyling = 'link linkOffset linkHoverWeighted';

	const accordionComponent = (children: JSX.Element | JSX.Element[], accordionHeader?: Field<string>, link?: JSX.Element) => {
		return (
			<Accordion activeEnd={1000} headerClass={styles.accordionBtn} bodyClass={styles.accordionBody}>
				<>
					<ChevronDownSvg className={styles.chevron}></ChevronDownSvg>

					<Text tag="h3" field={accordionHeader} className={styles.accordionHeading} />
				</>

				<>
					{link ? link : <></>}

					{children}
				</>
			</Accordion>
		);
	};

	const socialComponent = (field: LinkField, icon: JSX.Element) => {
		return (
			<li className={styles.item}>
				<Link field={field} className={`${styles.socialLink} noExternalSiteIcon`} title={field.value?.text}>
					<span className="screenReaderOnly">{field.value?.text}</span>

					<span className={styles.socialIcon}>{icon}</span>
				</Link>
			</li>
		);
	};

	return (
		<>
			<FooterBackToTop fields={props.fields} />

			<footer className={`${styles.section} blueDark`}>
				<div className={`${styles.outerGrid} maxWidth padYL padX`}>
					<div className={styles.innerGrid}>
						{props.fields.LinksColumn1 &&
							props.fields.LinksColumn1.length > 0 &&
							accordionComponent(
								<ul className={`${styles.list} listReset`}>
									{props.fields.LinksColumn1.map((item, index) => (
										<li key={`linkcolumn1-${index}`} className={styles.item}>
											{item.fields.Link?.value.anchor?.includes('cookie-modal') && (
												<Button
													key={`linkcolumn1-${index}`}
													title={item.fields.Link?.value?.title || 'Manage Cookie Preferences'}
													buttonRef={cookieManagementBtn}
													className={`${styles.modalTrigger} buttonReset link linkOffset linkHoverWeighted`}
													onClick={() => toggleBanner()}
												>
													{item.fields.Link?.value?.title || 'Manage cookie preferences'}
												</Button>
											)}
											{!item.fields.Link?.value?.anchor?.includes('cookie-modal') && (
												<Link field={item.fields.Link} className={listStyling} />
											)}
										</li>
									))}
								</ul>,
								props.fields.LinksColumn1Heading
							)}

						{props.fields.LinksColumn2 &&
							props.fields.LinksColumn2.length > 0 &&
							accordionComponent(
								<ul className={`${styles.list} listReset`}>
									{props.fields.LinksColumn2.map((item, index) => (
										<li key={`linkcolumn2-${index}`} className={styles.item}>
											<Link field={item.fields.Link} className={listStyling} />
										</li>
									))}
								</ul>,
								props.fields.LinksColumn2Heading
							)}

						{props.fields.LinksColumn3 &&
							props.fields.LinksColumn3.length > 0 &&
							accordionComponent(
								<ul className={`${styles.list} listReset`}>
									{props.fields.LinksColumn3.map((item, index) => (
										<li key={`linkcolumn3-${index}`} className={styles.item}>
											<Link field={item.fields.Link} className={listStyling} />
										</li>
									))}
								</ul>,
								props.fields.LinksColumn3Heading
							)}

						{accordionComponent(
							<>
								<ul className={`${styles.list} listReset ${styles.row}`}>
									{props.fields.FacebookLink.value?.href && socialComponent(props.fields.FacebookLink, <FacebookSvg />)}

									{props.fields.InstagramLink.value?.href && socialComponent(props.fields.InstagramLink, <InstagramSvg />)}

									{props.fields.LinkedInLink.value?.href && socialComponent(props.fields.LinkedInLink, <LinkedInSvg />)}

									{props.fields.YouTubeLink.value?.href && socialComponent(props.fields.YouTubeLink, <YouTubeSvg />)}
								</ul>

								<div className={`${styles.item} ${styles.desktopOnly}`}>
									{props.fields.RcnLogoLink && props.fields.RcnLogo && (
										<Link field={props.fields.RcnLogoLink} target="_blank" className="noExternalSiteIcon" rel="noreferrer">
											<Image className={styles.logo} media={props.fields.RcnLogo} loading="lazy" height={59} width={150} />
										</Link>
									)}

									<p className={styles.copyright}>
										{isEditing
											? props.fields.CopyrightText.value
											: props.fields.CopyrightText?.value.replace('[CurrentYear]', currentYear.toString())}
									</p>
								</div>
							</>,
							props.fields.ContactColumnHeading,
							<Link className={`${styles.contactRcn} link linkOffset linkHoverWeighted`} field={props.fields.ContactLink} />
						)}
					</div>

					<div className={`${styles.item} ${styles.belowDesktopOnly}`}>
						{props.fields.RcnLogoLink && props.fields.RcnLogo && (
							<Link field={props.fields.RcnLogoLink} target="_blank" className="noExternalSiteIcon" rel="noreferrer">
								<Image className={styles.logo} media={props.fields.RcnLogo} loading="lazy" height={59} width={150} />
							</Link>
						)}

						<p className={styles.copyright}>
							{isEditing
								? props.fields.CopyrightText.value
								: props.fields.CopyrightText?.value.replace('[CurrentYear]', currentYear.toString())}
						</p>
					</div>
				</div>
			</footer>
		</>
	);
}
